import {
  LegacyPortalShippingService,
  LegacyPortalShippingStatusDispatchStatus,
} from "@spwn-portal/grpc";
import useSWRImmutable from "swr/immutable";
import { useConnectClient } from "utility/connectWeb";

import type { PromiseClient } from "@bufbuild/connect";
import type { FulfillmentData } from "pages/AccountSettlementHistoryOrderId/types/FulfillmentData";

type UseOrderFulfillmentInput = {
  /** 配送（出庫）情報のFirestore ID。配送のない注文はnull */
  fulfillmentFirestoreId: string | null;
  orderId: string | null;
};

type UseOrderFulfillmentOutput = {
  fulfillmentData: FulfillmentData | null;
  isLoading: boolean;
};

const convertDispatchStatusToShippingStatus = (
  status: LegacyPortalShippingStatusDispatchStatus
): string => {
  switch (status) {
    case LegacyPortalShippingStatusDispatchStatus.PACKING:
      return "packing";
    case LegacyPortalShippingStatusDispatchStatus.SHIPPED:
      return "shipped";
    case LegacyPortalShippingStatusDispatchStatus.CANCELLED:
      return "cancelled";
    case LegacyPortalShippingStatusDispatchStatus.UNSPECIFIED:
    default:
      return "suspended";
  }
};

const fetchFulfillmentDataFromBigQuery = async (
  client: PromiseClient<typeof LegacyPortalShippingService>,
  fulfillmentFirestoreId: string
): Promise<FulfillmentData | null> => {
  try {
    const response = await client.getShippingStatusByFulfillmentFirestoreId({
      fulfillmentFirestoreId,
    });
    if (!response.shippingStatus) return null;

    const { status, trackingCode, trackingUrl, deliveryCarrier } =
      response.shippingStatus;

    return {
      carrier: deliveryCarrier,
      trackingCode: trackingCode ?? "",
      shippingStatus: convertDispatchStatusToShippingStatus(status),
      url: trackingUrl ?? "",
    };
  } catch (error) {
    console.error("Failed to fetch fulfillment data from BigQuery:", error);
    return null;
  }
};

export const useOrderFulfillment = (
  input: UseOrderFulfillmentInput
): UseOrderFulfillmentOutput => {
  const { fulfillmentFirestoreId, orderId } = input;
  const client = useConnectClient(LegacyPortalShippingService);

  const isFetch =
    fulfillmentFirestoreId !== null && fulfillmentFirestoreId !== "" && client;
  const { data: fulfillmentData = null, isLoading } =
    useSWRImmutable<FulfillmentData | null>(
      isFetch ? ["orderFulfillment", orderId, fulfillmentFirestoreId] : null,
      isFetch
        ? async () =>
            fetchFulfillmentDataFromBigQuery(client, fulfillmentFirestoreId)
        : null
    );

  return {
    fulfillmentData,
    isLoading,
  };
};
